import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, faSearch, faCheck, faUser, faBox,
  faChevronDown, faChevronRight, faList
} from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import { SupplierArticle } from '../../types/article';
import { getArticles, getAllArticleIds } from '../../services/articleApi';
import { getSuppliers } from '../../services/api';

interface ArticleSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  selectedArticles: string[];
  onSelectionChange: (articleIds: string[]) => void;
  title?: string;
  allowSupplierSelection?: boolean; // Erlaubt Auswahl ganzer Lieferanten
}

interface Supplier {
  _id: string;
  name: string;
  articles: SupplierArticle[];
  expanded: boolean;
}

const ArticleSelector: React.FC<ArticleSelectorProps> = ({
  isOpen,
  onClose,
  selectedArticles,
  onSelectionChange,
  title = 'Artikel auswählen',
  allowSupplierSelection = true
}) => {
  const [articles, setArticles] = useState<SupplierArticle[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'articles' | 'suppliers'>('articles');
  const [allAvailableArticleIds, setAllAvailableArticleIds] = useState<string[]>([]);
  const [loadingAllIds, setLoadingAllIds] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadArticles();
    }
  }, [isOpen]);

  const loadArticles = async () => {
    setLoading(true);
    try {
      // Lade echte Artikel-Daten von der API mit Paginierung (für Anzeige)
      const articlesResponse = await getArticles({
        limit: 100, // Reduziert auf 100 für bessere Performance
        page: 1
      });
      
      const realArticles = articlesResponse.articles || [];
      setArticles(realArticles);
      
      // Gruppiere Artikel nach Lieferanten
      const supplierGroups = realArticles.reduce((acc: { [key: string]: Supplier }, article: SupplierArticle) => {
        // Skip articles without supplierId
        if (!article.supplierId) {
          return acc;
        }
        
        const supplierId = typeof article.supplierId === 'string' 
          ? article.supplierId 
          : article.supplierId._id;
        const supplierName = typeof article.supplierId === 'string'
          ? `Lieferant ${supplierId.substring(0, 8)}`
          : article.supplierId.name;

        if (!acc[supplierId]) {
          acc[supplierId] = {
            _id: supplierId,
            name: supplierName,
            articles: [],
            expanded: false
          };
        }
        acc[supplierId].articles.push(article);
        return acc;
      }, {});

      setSuppliers(Object.values(supplierGroups));

      // Lade alle verfügbaren Artikel-IDs im Hintergrund (für "Alle auswählen")
      loadAllAvailableIds();
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllAvailableIds = async () => {
    setLoadingAllIds(true);
    try {
      const allIdsResponse = await getAllArticleIds({
        // Keine Filter - alle verfügbaren Artikel
      });
      
      if (allIdsResponse.success) {
        setAllAvailableArticleIds(allIdsResponse.articleIds);
        console.log(`📋 Loaded ${allIdsResponse.totalCount} total article IDs for bulk selection`);
      }
    } catch (error) {
      console.error('Error loading all article IDs:', error);
    } finally {
      setLoadingAllIds(false);
    }
  };

  const filteredArticles = articles.filter(article =>
    article.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    article.articleNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (article.description && article.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredSuppliers = suppliers.map(supplier => ({
    ...supplier,
    articles: supplier.articles.filter(article =>
      article.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.articleNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (article.description && article.description.toLowerCase().includes(searchTerm.toLowerCase()))
    )
  })).filter(supplier => 
    supplier.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    supplier.articles.length > 0
  );

  const handleArticleToggle = (articleId: string) => {
    const newSelection = selectedArticles.includes(articleId)
      ? selectedArticles.filter(id => id !== articleId)
      : [...selectedArticles, articleId];
    onSelectionChange(newSelection);
  };

  const handleSupplierToggle = (supplier: Supplier) => {
    const supplierArticleIds = supplier.articles.map(a => a._id);
    const allSelected = supplierArticleIds.every(id => selectedArticles.includes(id));
    
    if (allSelected) {
      // Deselektiere alle Artikel dieses Lieferanten
      const newSelection = selectedArticles.filter(id => !supplierArticleIds.includes(id));
      onSelectionChange(newSelection);
    } else {
      // Selektiere alle Artikel dieses Lieferanten
      const uniqueIds = new Set([...selectedArticles, ...supplierArticleIds]);
      const newSelection = Array.from(uniqueIds);
      onSelectionChange(newSelection);
    }
  };

  const toggleSupplierExpanded = (supplierId: string) => {
    setSuppliers(prev => prev.map(supplier => 
      supplier._id === supplierId 
        ? { ...supplier, expanded: !supplier.expanded }
        : supplier
    ));
  };

  const handleSelectAll = () => {
    // Wähle alle sichtbaren/gefilterten Artikel aus
    const allArticleIds = (viewMode === 'articles' ? filteredArticles : 
      filteredSuppliers.flatMap(s => s.articles)).map(a => a._id);
    
    if (allArticleIds.every(id => selectedArticles.includes(id))) {
      // Deselektiere alle sichtbaren
      const newSelection = selectedArticles.filter(id => !allArticleIds.includes(id));
      onSelectionChange(newSelection);
    } else {
      // Selektiere alle sichtbaren
      const uniqueIds = new Set([...selectedArticles, ...allArticleIds]);
      const newSelection = Array.from(uniqueIds);
      onSelectionChange(newSelection);
    }
  };

  const handleSelectAllArticles = async () => {
    // Wähle ALLE verfügbaren Artikel aus (nicht nur die sichtbaren)
    if (loadingAllIds) return;

    try {
      if (allAvailableArticleIds.length === 0) {
        // Falls noch nicht geladen, lade alle IDs
        setLoadingAllIds(true);
        const allIdsResponse = await getAllArticleIds({});
        if (allIdsResponse.success) {
          const allIds = allIdsResponse.articleIds;
          setAllAvailableArticleIds(allIds);
          
          // Prüfe ob alle bereits ausgewählt sind
          if (allIds.every(id => selectedArticles.includes(id))) {
            // Deselektiere alle
            onSelectionChange([]);
          } else {
            // Selektiere alle
            onSelectionChange(allIds);
          }
        }
        setLoadingAllIds(false);
      } else {
        // IDs bereits geladen
        if (allAvailableArticleIds.every(id => selectedArticles.includes(id))) {
          // Deselektiere alle
          onSelectionChange([]);
        } else {
          // Selektiere alle
          onSelectionChange(allAvailableArticleIds);
        }
      }
    } catch (error) {
      console.error('Error selecting all articles:', error);
      setLoadingAllIds(false);
    }
  };

  const handleSelectAllSuppliersWithArticles = async () => {
    // Wähle alle Lieferanten mit allen ihren Artikeln aus
    if (loadingAllIds) return;

    try {
      setLoadingAllIds(true);
      
      // Lade alle Artikel-IDs gruppiert nach Lieferanten
      const allIdsResponse = await getAllArticleIds({});
      if (allIdsResponse.success) {
        const allIds = allIdsResponse.articleIds;
        
        // Prüfe ob alle bereits ausgewählt sind
        if (allIds.every(id => selectedArticles.includes(id))) {
          // Deselektiere alle
          onSelectionChange([]);
        } else {
          // Selektiere alle
          onSelectionChange(allIds);
        }
      }
      setLoadingAllIds(false);
    } catch (error) {
      console.error('Error selecting all suppliers with articles:', error);
      setLoadingAllIds(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] flex flex-col shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          {/* Controls */}
          <div className="p-4 border-b bg-gray-50 space-y-3">
            {/* Search */}
            <div className="relative">
              <FontAwesomeIcon 
                icon={faSearch} 
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
              />
              <input
                type="text"
                placeholder="Artikel oder Lieferant suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex items-center justify-between">
              {/* View Mode Toggle */}
              {allowSupplierSelection && (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setViewMode('articles')}
                    className={`px-3 py-1 text-sm rounded ${
                      viewMode === 'articles' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <FontAwesomeIcon icon={faBox} className="mr-1" />
                    Artikel
                  </button>
                  <button
                    onClick={() => setViewMode('suppliers')}
                    className={`px-3 py-1 text-sm rounded ${
                      viewMode === 'suppliers' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    <FontAwesomeIcon icon={faUser} className="mr-1" />
                    Lieferanten
                  </button>
                </div>
              )}

              {/* Select All Options */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleSelectAll}
                  className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded flex items-center"
                >
                  <FontAwesomeIcon icon={faList} className="mr-1" />
                  Sichtbare auswählen/abwählen
                </button>
                
                <button
                  onClick={handleSelectAllArticles}
                  disabled={loadingAllIds}
                  className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 rounded flex items-center disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faBox} className="mr-1" />
                  {loadingAllIds ? 'Lädt...' : 'ALLE Artikel auswählen'}
                </button>
                
                <button
                  onClick={handleSelectAllSuppliersWithArticles}
                  disabled={loadingAllIds}
                  className="px-3 py-1 text-sm bg-green-100 hover:bg-green-200 rounded flex items-center disabled:opacity-50"
                >
                  <FontAwesomeIcon icon={faUser} className="mr-1" />
                  {loadingAllIds ? 'Lädt...' : 'ALLE Lieferanten'}
                </button>
              </div>
            </div>

            {/* Selection Info */}
            <div className="text-sm text-gray-600">
              <div>{selectedArticles.length} Artikel ausgewählt</div>
              {allAvailableArticleIds.length > 0 && (
                <div className="text-xs text-gray-500">
                  ({allAvailableArticleIds.length} Artikel insgesamt verfügbar)
                </div>
              )}
              {loadingAllIds && (
                <div className="text-xs text-blue-500">
                  Lade alle verfügbaren Artikel...
                </div>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="text-gray-500">Artikel werden geladen...</div>
              </div>
            ) : (
              <div className="space-y-2">
                {viewMode === 'articles' ? (
                  // Artikel-Ansicht
                  filteredArticles.map((article) => (
                    <div
                      key={article._id}
                      className={`border rounded-lg p-3 cursor-pointer transition-all ${
                        selectedArticles.includes(article._id)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => handleArticleToggle(article._id)}
                    >
                      <div className="flex items-center">
                        <div className={`w-4 h-4 border-2 rounded mr-3 flex items-center justify-center ${
                          selectedArticles.includes(article._id)
                            ? 'border-blue-500 bg-blue-500'
                            : 'border-gray-300'
                        }`}>
                          {selectedArticles.includes(article._id) && (
                            <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{article.name}</div>
                          <div className="text-sm text-gray-500">
                            {article.articleNumber} • {
                              !article.supplierId 
                                ? 'Unbekannter Lieferant'
                                : typeof article.supplierId === 'string' 
                                  ? `Lieferant ${article.supplierId.substring(0, 8)}`
                                  : article.supplierId.name
                            }
                          </div>
                          {article.description && (
                            <div className="text-sm text-gray-600 truncate">{article.description}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  // Lieferanten-Ansicht
                  filteredSuppliers.map((supplier) => (
                    <div key={supplier._id} className="border rounded-lg">
                      <div
                        className="p-3 flex items-center cursor-pointer hover:bg-gray-50"
                        onClick={() => toggleSupplierExpanded(supplier._id)}
                      >
                        <FontAwesomeIcon 
                          icon={supplier.expanded ? faChevronDown : faChevronRight} 
                          className="text-gray-400 mr-2" 
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{supplier.name}</div>
                          <div className="text-sm text-gray-500">
                            {supplier.articles.length} Artikel
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSupplierToggle(supplier);
                          }}
                          className={`px-3 py-1 text-sm rounded ${
                            supplier.articles.every(a => selectedArticles.includes(a._id))
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          Alle auswählen
                        </button>
                      </div>

                      {supplier.expanded && (
                        <div className="border-t bg-gray-50">
                          {supplier.articles.map((article) => (
                            <div
                              key={article._id}
                              className={`p-3 ml-6 cursor-pointer transition-all ${
                                selectedArticles.includes(article._id)
                                  ? 'bg-blue-50 border-l-2 border-blue-500'
                                  : 'hover:bg-gray-100'
                              }`}
                              onClick={() => handleArticleToggle(article._id)}
                            >
                              <div className="flex items-center">
                                <div className={`w-4 h-4 border-2 rounded mr-3 flex items-center justify-center ${
                                  selectedArticles.includes(article._id)
                                    ? 'border-blue-500 bg-blue-500'
                                    : 'border-gray-300'
                                }`}>
                                  {selectedArticles.includes(article._id) && (
                                    <FontAwesomeIcon icon={faCheck} className="text-white text-xs" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">{article.name}</div>
                                  <div className="text-sm text-gray-500">{article.articleNumber}</div>
                                  {article.description && (
                                    <div className="text-sm text-gray-600 truncate">{article.description}</div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))
                )}

                {(viewMode === 'articles' ? filteredArticles : filteredSuppliers).length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    {searchTerm ? 'Keine Artikel gefunden' : 'Keine Artikel verfügbar'}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-4 border-t bg-gray-50">
            <div className="text-sm text-gray-600">
              {selectedArticles.length} Artikel ausgewählt
            </div>
            <div className="flex space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Auswählen
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ArticleSelector;
